<template>
    <section>
        <h2>Categorise Source System Statuses</h2>
        <div class="status-mapping-help-text">
            <b>
                Configure how source system statuses drive assignment behaviour in Playvox.
            </b>
            <ul class="status-mapping-list">
                <li>
                    <p>
                        <span class="status-mapping-bold-text">Workstream Completion -</span>
                        Use for statuses where the workstream is permanently closed. 
                        The current assignment will close, and no new assignments are created.
                    </p>
                </li>
                <li>
                    <p>
                        <span class="status-mapping-bold-text">Is Pending -</span>
                        Use for statuses where the workstream is waiting on the customer. 
                        The current assignment will close, and a new one is created when the workstream reopens 
                        (same or new group, depending on assignment rules).
                    </p>
                </li>
                <li>
                    <p>
                        <span class="status-mapping-bold-text">Status Completion -</span>
                        Use for statuses where the workstream is temporarily closed (e.g. Solved). 
                        If reopened, a new assignment is created for the assigned group.
                    </p>
                </li>
            </ul>
        </div>
        <ag-grid-vue
            :suppress-scroll-on-new-data="true"
            :suppress-row-click-selection="true"
            style="height: 300px"
            class="ag-theme-quartz"
            :column-defs="columnDefinitions"
            :row-data="rowData"
            row-selection="multiple"
            :modules="modules"
            :on-grid-ready="onGridReady"
        />
        <div
            class="status-mapping-save"
            v-if="canEdit"
        >
            <al-button
                class="save-button status-mapping-save-button"
                variant="primary"
                @click="saveStatusDefinitions"
            >
                Save
            </al-button>
        </div>
    </section>
</template>
<script setup lang="ts">
import { AgGridVue } from '@ag-grid-community/vue3';
import {
    getStatusDefinitions,
    putStatusDefinitions,
    StatusDefinitions
} from './statusApi';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import {
    Module, GridApi, GridReadyEvent
} from '@ag-grid-community/core';
import { RowData } from '@/types';
import {
    ref, onMounted, computed
} from 'vue';
import { state } from '@/utils/state';
import _ from 'lodash';
import { useACLs } from '@agyletime/vue-3-common/src/composables/acl';

import StatusMappingCheckbox from './StatusMappingCheckbox.vue';
const modules = [ClientSideRowModelModule] as Module[];
const rowData = ref<StatusDefinitions>([]);
const gridApi = ref<GridApi<RowData> | null>(null);
const acl = useACLs();
const canEdit = computed(() => acl.can.edit_operations_configurations);
const onGridReady = (evt: GridReadyEvent) => {
    evt.api.sizeColumnsToFit();
    gridApi.value = evt.api;
};
const columnDefinitions = computed(() => [
    {
        headerName: 'Status',
        field: 'status',
        resizable: true,
        headerClass: 'text-center'
    },
    {
        headerName: 'Workstream Completion',
        field: 'isWorkstreamCompletion',
        cellRenderer: StatusMappingCheckbox,
        cellRendererParams: { gridApi, canEdit },
        headerClass: 'text-center'
    },
    {
        headerName: 'Is Pending',
        field: 'isPendingCustomer',
        cellRenderer: StatusMappingCheckbox,
        cellRendererParams: { gridApi, canEdit },
        headerClass: 'text-center'
    },
    {
        headerName: 'Status Completion',
        field: 'isStatusCompletion',
        cellRenderer: StatusMappingCheckbox,
        cellRendererParams: { gridApi, canEdit },
        headerClass: 'text-center'
    }
]);
const saveStatusDefinitions = async () => {
    try {
        await putStatusDefinitions(rowData.value);
        return state.toasts.push({
            message: 'Successfully saved Status Definitions',
            id: _.uniqueId(),
            type: 'SUCCESS'
        });
    } catch (err) {
        return state.toasts.push({
            message: `Failed to save Status Definitions ${err}`,
            id: _.uniqueId(),
            type: 'ERROR'
        });
    }
};
onMounted(async () => {
    const statusDefinitions = await getStatusDefinitions();
    rowData.value = statusDefinitions;
});
</script>
<style scoped lang="scss">
section {
  margin-top: 5rem;
}
p {
    margin-block-start: 0;
    margin-block-end: 0;
}
.status-mapping-bold-text {
    font-weight: bold;
}
.status-mapping-list {
    padding-inline-start: 16px;
    margin: 0;
    li {
        margin-top: 5px;
    }
}
.status-mapping-help-text {
    margin-bottom: 1rem;
}
.ag-header-cell-label {
    justify-content: center;
}
.status-mapping-save {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
}
</style>
